/**
 * Global Search Service
 * ---------------------
 * Wraps the Watch API hybrid search endpoint used by GlobalSearch.vue.
 *
 * The Watch API provides a single hybrid search endpoint that combines
 * lexical (FTS5) and semantic (cosine similarity) retrieval:
 *
 *   GET /search?q={query}                    — corpus-wide
 *   GET /search?q={query}&session_id={id}    — single session
 *
 * Response shape:
 *   {
 *     data: Array<{
 *       session_id:      string,
 *       frame_id:        number,
 *       timestamp_ms:    number,
 *       snippet:         string,
 *       score:           number,
 *       risk_indicators: string[],
 *       provenance: {
 *         event:  string,   // "command" | "text" | "analysis" | "session"
 *         engine: string,   // "guac" | "ocr" | "vlm" | "llm" | "semantic" | "lexical"
 *         method: string,   // "local" | "global" | "semantic"
 *       }
 *     }>,
 *     count: number,
 *   }
 *
 * Results are grouped by type for the GlobalSearch dropdown:
 *   commands → provenance.event === "command"
 *   vision   → provenance.engine === "ocr" | "vlm"
 *   sessions → provenance.method === "global"
 */

import { apiClient } from "./apiClient";

// ---------------------------------------------------------------------------
// Core search
// ---------------------------------------------------------------------------

/**
 * Executes a hybrid search against the Watch API.
 *
 * @param {string}  query
 * @param {object}  [options]
 * @param {string}  [options.sessionId]  - Scope to a single session
 * @param {number}  [options.limit=20]
 * @param {number}  [options.threshold]  - Semantic similarity threshold
 * @returns {Promise<{ data: Array, count: number }>}
 */
export async function search(query, { sessionId, limit = 20, threshold } = {}) {
  if (!query || query.trim().length < 2) {
    throw new Error("Query must be at least 2 characters");
  }

  const params = new URLSearchParams({ q: query.trim(), limit: String(limit) });
  if (sessionId)  params.set("session_id", sessionId);
  if (threshold !== undefined) params.set("threshold", String(threshold));

  return apiClient(`/search?${params}`);
}

// ---------------------------------------------------------------------------
// Grouped search (for GlobalSearch.vue dropdown)
// ---------------------------------------------------------------------------

/**
 * Runs a global search and groups results by category for the dropdown UI.
 *
 * Returns an object compatible with the GlobalSearch.vue computed properties:
 *   commandResults, visionResults, sessionResults
 *
 * @param {string}  query
 * @param {object}  [options]
 * @param {number}  [options.limit=5]      - Max results per category
 * @param {number}  [options.threshold]
 * @returns {Promise<{
 *   query:        string,
 *   totalResults: number,
 *   results: Array<{ type: string, results: Array, total: number }>
 * }>}
 */
export async function globalSearch(_ignoredBaseUrl, query, options = {}) {
  const { limit = 5, threshold } = options;

  if (!query || query.trim().length < 2) {
    return { query, totalResults: 0, results: [] };
  }

  let raw;
  try {
    raw = await search(query, { limit: 50, threshold });
  } catch (err) {
    console.error("[globalSearchService] Search failed:", err);
    return { query, totalResults: 0, results: [], error: err.message };
  }

  const items = raw?.data ?? [];

  // Group by provenance type
  const commands = items
    .filter((r) => r.provenance?.event === "command")
    .slice(0, limit)
    .map(_toCommandResult);

  const vision = items
    .filter((r) =>
      r.provenance?.engine === "ocr" || r.provenance?.engine === "vlm"
    )
    .slice(0, limit)
    .map(_toVisionResult);

  const sessions = items
    .filter((r) => r.provenance?.method === "global")
    .slice(0, limit)
    .map(_toSessionResult);

  const totalResults = commands.length + vision.length + sessions.length;

  return {
    query:        query.trim(),
    totalResults,
    elapsed:      0,
    results: [
      { type: "commands", results: commands, total: commands.length },
      { type: "vision",   results: vision,   total: vision.length   },
      { type: "sessions", results: sessions, total: sessions.length },
    ].filter((g) => g.total > 0),
  };
}

// ---------------------------------------------------------------------------
// Result normalizers
// ---------------------------------------------------------------------------

function _toCommandResult(item) {
  return {
    job_id:    item.session_id,
    command:   item.snippet,
    timestamp: item.timestamp_ms / 1000,
    frame_id:  item.frame_id,
    score:     item.score,
  };
}

function _toVisionResult(item) {
  return {
    job_id:   item.session_id,
    text:     item.snippet,
    frame:    String(item.frame_id),
    frame_id: item.frame_id,
    score:    item.score,
  };
}

function _toSessionResult(item) {
  return {
    job_id:   item.session_id,
    protocol: "RDP",           // not available at search level
    duration: "--:--",         // not available at search level
    score:    item.score,
  };
}

// ---------------------------------------------------------------------------
// Display helpers (used by GlobalSearch.vue)
// ---------------------------------------------------------------------------

/**
 * Formats a timestamp in seconds to MM:SS.
 *
 * @param {number} seconds
 * @returns {string}
 */
export function formatTimestamp(seconds) {
  if (!seconds || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

/**
 * Truncates a session ID for display in the results dropdown.
 *
 * @param {string} sessionId
 * @returns {string}
 */
export function formatJobId(sessionId) {
  if (!sessionId) return "Unknown";
  if (sessionId.length <= 20) return sessionId;
  return `${sessionId.slice(0, 8)}…${sessionId.slice(-6)}`;
}

// ---------------------------------------------------------------------------
// Search history (localStorage)
// ---------------------------------------------------------------------------

const HISTORY_KEY = "watch_search_history";
const MAX_HISTORY = 10;

/**
 * Saves a search query to local history.
 *
 * @param {string} query
 * @param {number} resultCount
 */
export function saveSearchHistory(query, resultCount) {
  if (!query || query.length < 2) return;
  try {
    const history = getSearchHistory().filter((h) => h.query !== query);
    history.unshift({ query, resultCount, timestamp: Date.now() });
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
  } catch {}
}

/**
 * Returns the saved search history.
 *
 * @returns {Array<{ query: string, resultCount: number, timestamp: number }>}
 */
export function getSearchHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]");
  } catch {
    return [];
  }
}

/**
 * Clears all saved search history.
 */
export function clearSearchHistory() {
  localStorage.removeItem(HISTORY_KEY);
}

export default {
  search,
  globalSearch,
  formatTimestamp,
  formatJobId,
  saveSearchHistory,
  getSearchHistory,
  clearSearchHistory,
};